import { motion } from 'framer-motion';
import React, { MouseEvent, useRef, useState } from 'react';
import './AnimatedButton.css';

interface AnimatedButtonProps {
  variant?:
    | 'primary'
    | 'secondary'
    | 'danger'
    | 'success'
    | 'warning'
    | 'info'
    | 'outline';
  size?: 'small' | 'medium' | 'large';
  children: React.ReactNode;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  loading?: boolean;
  [key: string]: any;
}

interface MousePosition {
  x: number;
  y: number;
}

const AnimatedButton: React.FC<AnimatedButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  children,
  onClick,
  disabled = false,
  type = 'button',
  className = '',
  loading = false,
  ...props
}) => {
  const [mousePos, setMousePos] = useState<MousePosition>({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Animation variants for Framer Motion
  const animationVariants = {
    initial: {
      scale: 1,
      opacity: 1,
    },
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.2,
        ease: [0.4, 0, 0.2, 1] as any,
      },
    },
    tap: {
      scale: 0.95,
      transition: {
        duration: 0.1,
        ease: [0.4, 0, 0.2, 1] as any,
      },
    },
    disabled: {
      scale: 1,
      opacity: 0.5,
    },
  };

  // Ripple animation variants
  const rippleVariants = {
    initial: {
      opacity: 0,
    },
    hover: {
      opacity: 1,
      transition: { duration: 0.3 },
    },
  };

  // Glare animation variants
  const glareVariants = {
    initial: {
      opacity: 0,
      x: '-50%',
      y: '-50%',
    },
    hover: {
      opacity: 0.4,
      x: `calc(-50% + ${mousePos.x}px)`,
      y: `calc(-50% + ${mousePos.y}px)`,
      transition: {
        duration: 0.2,
        ease: [0.4, 0, 0.2, 1] as any,
      },
    },
  };

  // Mouse event handlers
  const handleMouseEnter = (e: MouseEvent<HTMLButtonElement>) => {
    setIsHovered(true);
    updateMousePosition(e);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setMousePos({ x: 0, y: 0 });
  };

  const handleMouseMove = (e: MouseEvent<HTMLButtonElement>) => {
    if (isHovered) {
      updateMousePosition(e);
    }
  };

  const updateMousePosition = (e: MouseEvent<HTMLButtonElement>) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      setMousePos({ x, y });
    }
  };

  // Generate CSS classes
  const buttonClasses = [
    'animated-button',
    `animated-button--${variant}`,
    `animated-button--${size}`,
    loading && 'animated-button--loading',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <motion.button
      ref={buttonRef}
      type={type}
      className={buttonClasses}
      variants={animationVariants}
      initial="initial"
      whileHover={!disabled && !loading ? 'hover' : 'disabled'}
      whileTap={!disabled && !loading ? 'tap' : 'disabled'}
      onClick={!disabled && !loading ? onClick : undefined}
      disabled={disabled || loading}
      onMouseEnter={!disabled && !loading ? handleMouseEnter : undefined}
      onMouseLeave={!disabled && !loading ? handleMouseLeave : undefined}
      onMouseMove={!disabled && !loading ? handleMouseMove : undefined}
      {...props}
    >
      {/* Ripple effect overlay */}
      <motion.div
        className="animated-button__ripple"
        variants={rippleVariants}
        initial="initial"
        whileHover={!disabled && !loading ? 'hover' : 'initial'}
      />

      {/* Glare effect overlay */}
      <motion.div
        className="animated-button__glare"
        variants={glareVariants}
        initial="initial"
        animate={isHovered && !disabled && !loading ? 'hover' : 'initial'}
      />

      {/* Button content */}
      <motion.span
        className="animated-button__content"
        initial={{ opacity: 1 }}
        animate={{ opacity: disabled ? 0.6 : 1 }}
      >
        {children}
      </motion.span>
    </motion.button>
  );
};

export default AnimatedButton;
